import { neueBestellungOeffnen, neueBestellungSchliessen } from '@/lib/neueBestellung';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { heuteIso } from '@/lib/format';
import { useAuth } from '@/hooks/useAuth';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { mobilMenueOeffnen, mobilMenueSchliessen } from '@/lib/mobilMenue';
import { KAPITEL, alleSchritte, schritteVon, type KapitelId, type TourAktion } from './tour';
import { starteLauf } from './tourLauf';
import { setzeUebungsModus } from './uebungsModus';

type UebungAntwort = { bestellung: { id: number; bestellnr: string } | null };

/**
 * Setzt die Scrollposition der Bestellliste und des Fensters.
 *
 * Die Tabelle scrollt in ihrem eigenen Kasten, nicht im Fenster —
 * `window.scrollTo` allein reicht deshalb nicht.
 */
function setzeTabellenScroll(oben: number): void {
  window.scrollTo({ top: oben });
  const tabelle = document.querySelector('[data-tour="dashboard-tabelle"]');
  tabelle?.parentElement?.scrollTo({ top: oben });
}

type TourSteuerung = {
  laeuft: boolean;
  starte: (kapitel?: KapitelId) => void;
  brich: () => void;
};

const Ctx = createContext<TourSteuerung | null>(null);

export function useTour(): TourSteuerung {
  const ctx = useContext(Ctx);
  if (ctx === null) throw new Error('useTour außerhalb TourProvider');
  return ctx;
}

/**
 * Hält die laufende Tour und verbindet sie mit der Anwendung.
 *
 * Die Tour selbst kennt weder Router noch API — sie sagt nur, WAS
 * passieren soll. Übersetzt wird das hier.
 */
export function TourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const router = useRouter();
  const qc = useQueryClient();
  const auth = useAuth();
  const pfad = useRouterState({ select: (s) => s.location.pathname });
  const [laeuft, setLaeuft] = useState(false);
  const { confirm, dialog: abbruchDialog } = useConfirm();
  const abbrechenRef = useRef<(() => void) | null>(null);
  // Nummer der laufenden Uebung. Gebraucht wird sie beim Aufraeumen:
  // steht man gerade auf ihrer Detailseite, muss man vorher weg — sonst
  // endet die Tour auf "Bestellung konnte nicht geladen werden".
  const uebungNrRef = useRef<string | null>(null);

  // Der Puls der Tour liest den Pfad häufig; über ein Ref bleibt er
  // aktuell, ohne dass der Lauf neu aufgesetzt werden muss.
  const pfadRef = useRef(pfad);
  pfadRef.current = pfad;

  const uebungHolen = useCallback(async (): Promise<string | null> => {
    const res = await apiFetch<UebungAntwort>('/onboarding/uebung.php');
    return res.bestellung?.bestellnr ?? null;
  }, []);

  const uebungAnlegen = useCallback(async (): Promise<string> => {
    const res = await apiFetch<{ bestellung: { bestellnr: string } }>(
      '/onboarding/uebung.php',
      { method: 'POST', body: JSON.stringify({}) }
    );
    await qc.invalidateQueries({ queryKey: ['bestellungen'] });
    return res.bestellung.bestellnr;
  }, [qc]);

  /**
   * Raeumt die eigene Uebungsbestellung weg. Vorher weg von ihrer
   * Detailseite, falls man gerade dort steht.
   */
  const aufraeumen = useCallback(async (): Promise<void> => {
    const nr = uebungNrRef.current;
    if (nr !== null && pfadRef.current.includes(`/bestellungen/${nr}`)) {
      await navigate({ to: '/' });
    }
    uebungNrRef.current = null;
    // Aufräumen heißt: den Bildschirm so hinterlassen, wie die Tour ihn
    // vorgefunden hat. Auf dem Telefon steht sonst am Ende noch das
    // Menü offen, das der Hilfe-Schritt aufgezogen hat.
    mobilMenueSchliessen();
    await apiFetch('/onboarding/aufraeumen.php', {
      method: 'POST',
      body: JSON.stringify({})
    });
    // Erst den zwischengespeicherten Detailstand wegwerfen, dann die
    // Liste neu holen. Ohne das erste blieb die geloeschte Uebung im
    // Cache und tauchte beim naechsten Aufruf noch einmal auf; ohne das
    // zweite stand sie bis zum naechsten Neuladen als Geisterzeile im
    // Dashboard (B96).
    qc.removeQueries({ queryKey: ['bestellung'] });
    await qc.refetchQueries({ queryKey: ['bestellungen'], type: 'active' });
    setzeTabellenScroll(0);
  }, [navigate, qc]);

  const aktion = useCallback(
    async (was: TourAktion): Promise<void> => {
      switch (was) {
        case 'zum-dashboard':
          if (pfadRef.current !== '/') {
            await navigate({ to: '/' });
          }
          return;

        case 'sheet-oeffnen':
          // Falls jemand den Schritt überspringt, statt selbst zu klicken:
          // das Formular gehört trotzdem geöffnet, sonst zeigen die
          // folgenden Schritte ins Leere.
          neueBestellungOeffnen(navigate as never);
          return;

        case 'sheet-schliessen':
          neueBestellungSchliessen(router, navigate as never);
          return;

        case 'menue-oeffnen':
          // Am Schreibtisch hängt kein Menü im Baum — dann ist das hier
          // wirkungslos, und Nutzerwechsel wie Hilfe stehen ohnehin da.
          mobilMenueOeffnen();
          return;

        case 'uebung-sicherstellen': {
          const vorhanden = await uebungHolen();
          const nr = vorhanden ?? (await uebungAnlegen());
          uebungNrRef.current = nr;
          if (!pfadRef.current.includes(`/bestellungen/${nr}`)) {
            await navigate({ to: '/bestellungen/$bestellnr', params: { bestellnr: nr } });
          }
          return;
        }

        case 'uebung-neu': {
          const nr = await uebungAnlegen();
          uebungNrRef.current = nr;
          await navigate({ to: '/bestellungen/$bestellnr', params: { bestellnr: nr } });
          return;
        }

        case 'aufraeumen':
          await aufraeumen();
          return;
      }
    },
    [navigate, router, aufraeumen, uebungHolen, uebungAnlegen]
  );

  const brich = useCallback(() => {
    abbrechenRef.current?.();
  }, []);

  const starte = useCallback(
    (kapitel?: KapitelId) => {
      abbrechenRef.current?.();

      const gewaehlt = kapitel === undefined ? null : (KAPITEL.find((k) => k.id === kapitel) ?? null);
      const schritte =
        gewaehlt === null ? alleSchritte() : schritteVon(gewaehlt);
      if (schritte.length === 0) return;

      // Ein einzeln gestartetes Übungs-Kapitel bekommt eine frische
      // Übungsbestellung: wer nur den Lebenszyklus nachschlagen will,
      // soll nicht an der halb fertigen von vorhin weitermachen.
      const einzelnesUebungsKapitel = gewaehlt?.brauchtUebung ?? false;
      const auszufuehren = einzelnesUebungsKapitel
        ? schritte.map((s, i) =>
            i === 0 && s.vorher === 'uebung-sicherstellen' ? { ...s, vorher: 'uebung-neu' as const } : s
          )
        : schritte;

      setzeUebungsModus(true);
      setLaeuft(true);
      // Von oben anfangen. Wer die Tour aus einer weit gescrollten
      // Liste heraus startet, saehe sonst den ersten Schritt an einer
      // Stelle, an der er nichts erklaeren kann (B102).
      setzeTabellenScroll(0);

      // Mit sauberem Tisch anfangen. Ohne das sammeln sich Übungen an:
      // Kapitel 2 legt eine über das echte Formular an, und ein
      // abgebrochener Anlauf von vorhin liegt womöglich noch da. Beim
      // Durchspielen im Build standen so zwei gleichzeitig auf dem Brett.
      void aufraeumen().catch((f) =>
        console.warn('[Tour] Alte Übung ließ sich nicht wegräumen:', f)
      );

      abbrechenRef.current = starteLauf(auszufuehren, {
        aktion,
        pfad: () => pfadRef.current,
        // Das Kreuz beendet nicht kommentarlos: wer versehentlich
        // danebentippt, hat sonst die halbe Übung verloren (B106).
        abbruchBestaetigen: () =>
          confirm({
            titel: 'Tour beenden?',
            text: 'Die Übungsbestellung wird dabei entfernt. Über „Hilfe“ kannst du jederzeit wieder einsteigen.',
            bestaetigenLabel: 'Tour beenden'
          }),
        fertig: (durchgelaufen) => {
          setzeUebungsModus(false);
          setLaeuft(false);
          abbrechenRef.current = null;
          // Immer aufräumen, nicht nur beim Abbruch: ein einzeln
          // gestartetes Kapitel hat keinen Schluss-Schritt, und ein
          // zweiter Aufruf nach dem Durchlauf löscht schlicht nichts
          // mehr. Eine liegengebliebene Übung wäre der schlimmere Fall.
          void aufraeumen().catch((f) =>
            console.warn('[Tour] Aufräumen fehlgeschlagen:', f)
          );
          // Die vollständige Tour gilt als erledigt; ein einzelnes
          // Kapitel aus der Hilfe ändert am Onboarding-Stand nichts.
          if (durchgelaufen && kapitel === undefined) {
            void apiFetch('/onboarding/status.php', {
              method: 'POST',
              body: JSON.stringify({ aktion: 'abgeschlossen', heute: heuteIso() })
            })
              .then(() => qc.invalidateQueries({ queryKey: ['auth'] }))
              .catch((f) => console.warn('[Tour] Abschluss konnte nicht gemerkt werden:', f));
          }
        }
      });
    },
    [aktion, aufraeumen, confirm, qc]
  );

  // Sicherheitsnetz: verlässt jemand die Seite mitten in der Tour, bleibt
  // sonst eine Übungsbestellung liegen. Der Server räumt sie spätestens
  // nach einer Woche weg — das hier ist der schnelle Weg.
  useEffect(() => {
    if (!laeuft) return;
    const beiVerlassen = (): void => {
      navigator.sendBeacon?.('/api/onboarding/aufraeumen.php', new Blob(['{}']));
    };
    window.addEventListener('pagehide', beiVerlassen);
    return () => window.removeEventListener('pagehide', beiVerlassen);
  }, [laeuft]);

  // Angemeldet, aber noch nie durch die Tour: einmal anbieten.
  const angeboten = useRef(false);
  const [angebot, setAngebot] = useState(false);
  useEffect(() => {
    if (angeboten.current) return;
    if (auth.data === undefined) return;
    if (auth.data.user.onboarding_abgeschlossen_am !== null) return;
    angeboten.current = true;
    setAngebot(true);
  }, [auth.data]);

  return (
    <Ctx.Provider value={{ laeuft, starte, brich }}>
      {children}
      {abbruchDialog}
      {angebot && !laeuft && (
        <Willkommen
          onStart={() => {
            setAngebot(false);
            starte();
          }}
          onSpaeter={() => setAngebot(false)}
          onNie={() => {
            setAngebot(false);
            void apiFetch('/onboarding/status.php', {
              method: 'POST',
              body: JSON.stringify({ aktion: 'abgeschlossen', heute: heuteIso() })
            }).then(() => qc.invalidateQueries({ queryKey: ['auth'] }));
          }}
        />
      )}
    </Ctx.Provider>
  );
}

function Willkommen({
  onStart,
  onSpaeter,
  onNie
}: {
  onStart: () => void;
  onSpaeter: () => void;
  onNie: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-labelledby="onboarding-titel"
      data-tour="willkommen"
      // Über der Bottom-Navigation, nicht darunter: die Leiste ist
      // rund 68 Pixel hoch und läge sonst auf den Knöpfen „Später" und
      // „Tour starten".
      className="fixed bottom-4 right-4 z-[70] w-[min(28rem,calc(100vw-2rem))] rounded-md border border-line bg-cream p-5 shadow-lg max-md:bottom-[calc(5.25rem+env(safe-area-inset-bottom))] max-md:left-4 max-md:w-auto"
    >
      <h2 id="onboarding-titel" className="font-serif text-lg text-ink">
        Zum ersten Mal hier?
      </h2>
      <p className="mt-2 text-sm text-ink-soft">
        In etwa zehn Minuten zeigt dir eine kurze Tour, wie eine Bestellung bei uns
        durchläuft — mit einer Übungsbestellung, die nur du siehst und die danach
        wieder verschwindet.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onStart}
          className="inline-flex min-h-[44px] items-center rounded-md bg-akzent px-4 py-2.5 text-sm font-medium text-cream hover:bg-akzent-stark"
        >
          Tour starten
        </button>
        {/* "Später" ist eine echte zweite Wahl und sieht auch so aus.
            Vorher war es ein blasser Textknopf neben einem Textlink —
            beides gleich unauffällig, und man traf das Falsche (B109). */}
        <button
          type="button"
          onClick={onSpaeter}
          className="inline-flex min-h-[44px] items-center rounded-md border border-line bg-cream-dark px-4 py-2.5 text-sm text-ink hover:bg-gold-soft"
        >
          Später
        </button>
      </div>
      {/* Die Tragweite steht im Wortlaut, nicht in einer Rückfrage: über
          „Hilfe“ lässt sich die Begrüßung jederzeit wieder einschalten. */}
      <button
        type="button"
        onClick={onNie}
        className="mt-3 block text-xs text-ink-faint underline-offset-2 hover:text-ink-soft hover:underline"
      >
        Ich kenne das schon — nicht mehr zeigen
      </button>
    </div>
  );
}
